/**
 * @architecture-header 1
 * @layer module
 * @domain assistants
 * @responsibility Define what a usable AI assistant persona must contain before it can be saved.
 * @state-owner React Hook Form
 * @allowed-imports Zod
 * @forbidden Zustand; API client; routes
 * @public-api assistants module forms
 * @side-effects none
 * @tests modules/assistants/model/assistant.test.ts
 */

import { z } from 'zod';

const trait = z.number().int().min(0).max(100);

export const replyRuleSchema = z.object({
  id: z.string().min(1),
  trigger: z.string().trim().min(1, '請寫學生會說的話'),
  reply: z.string().trim().min(1, '請寫助教要回的話'),
});

export const assistantSchema = z.object({
  id: z.string().min(1),
  name: z.string().trim().min(1, '請給助教一個名字').max(20, '名字請短一點'),
  emoji: z.string().trim().min(1, '請選一個頭像'),
  accent: z.string().trim().min(1),
  tagline: z.string().trim().max(40, '一句話介紹請短一點'),
  persona: z
    .string()
    .trim()
    .min(10, '角色設定至少寫十個字，助教才知道自己是誰')
    .max(600, '角色設定太長，模型會記不住重點'),
  tone: z.enum(['warm', 'calm', 'playful', 'coach']),
  warmth: trait,
  humour: trait,
  strictness: trait,
  brevity: trait,
  address: z.string().trim().min(1, '請寫要怎麼稱呼學生'),
  catchphrase: z.string().trim().max(20, '口頭禪請短一點'),
  greeting: z.string().trim().min(1, '請寫開場白'),
  emojiUse: z.enum(['none', 'light', 'lots']),
  hintsBeforeTeacher: z.number().int().min(1).max(5),
  answerPolicy: z.enum(['never', 'after-hints']),
  forbidden: z.string().trim(),
  replyRules: z.array(replyRuleSchema),
  isDefault: z.boolean(),
});

export type AssistantValues = z.infer<typeof assistantSchema>;
